import React, { useCallback, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
    CrossIcon,
    StyledLabel,
    StyledTextInput,
    CheckboxIcon,
    StyledSelectInput,
} from "@aureskonnect/react-ui";
import { AvForm, AvGroup } from "availity-reactstrap-validation";
import { toast } from "react-toastify";
import classnames from "classnames";
import { useSnapshot } from "valtio";
import { Select } from "antd";
import ErrorMessage from "../ErrorMessage";
import { clearMultiCustomSelectionData } from "@components/Common/MultiCustomSelect/store";
import { clearData } from "@components/Common/MultiCustomCreatableSelection/store";
import {
    store as device,
    setIsActionsClicked,
    setActions,
    setIsEdited,
    setIsPlusIconClicked,
} from "./store";
import {
    CustomCardBody,
    CustomCardFooter,
    CustomCardHeader,
} from "@components/Common/CustomCardStyled";
import { ConfirmationEditMessage } from "@components/Common/ConfirmationEditMessage";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import "@assets/swal.css";
import "@assets/theme.css";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import {
    createDataVille,
    PostCity,
    INITIAL_CITY_FORM_DATA,
    INITIAL_VALIDATION,
    validateAndSubmit,
    formatCityLivraisonData,
} from "./hooks";
import {
    CountryPostalCodes,
    fetchPays,
    getShopVatList,
    NUMBER_REGEX,
    PostalCodeOption,
    postalCodeFilterOption,
    removeLeadingZeros,
    resolveDefaultTva,
    resolveTvaFromList,
    scrollToFirstError,
} from "../shared";
import ErrorToast from "@components/Common/ErrorTost";
import { setOrders, store as shopStore } from "../../store";
import { useFetchGlobal } from "../../../../../../api/hooks";
import { store as storeGlobal } from "@components/VerticalLayout/store";

type ModeOfSaleFormType = {
    isAddCity: boolean;
    setDataIsUpdated: React.Dispatch<React.SetStateAction<boolean | number>>;
    selectedRows: any;
    setLocalFilterActive: Function;
    setIsAddCity: Function;
    isAddNewDeviseButtonClicked: boolean;
    setSelectedRows: Function;
    selectedRowShops: any;
    cityData: any;
};

export default function CityForm({
    setIsAddCity,
    isAddCity,
    setDataIsUpdated,
    selectedRows,
    setLocalFilterActive,
    isAddNewDeviseButtonClicked,
    setSelectedRows,
    selectedRowShops,
    cityData,
}: ModeOfSaleFormType) {
    const { t } = useTranslation();
    const { franchiseID } = useSnapshot(storeGlobal);

    const { fetchGlobal } = useFetchGlobal();

    let pays: any = useSelector((state: any) => state.global.pays);
    const list = getShopVatList(selectedRowShops).map((el) => el.value);
    const listCode = (cityData?.data ?? []).map(
        (el: any) =>
            `${el[t("Postal code")] || el["Code Postal"]}_${
                el[t("City")] || el["Ville"]
            }`
    );

    const { isActionsClicked, isEdited } = useSnapshot(device);
    const i18nextLng = localStorage.getItem("i18nextLng") || "fr";
    const [
        isClickedBouttonValider,
        setIsClickedBouttonValider,
    ] = React.useState<boolean>(true);
    const [cityFormData, setCityFormData] = React.useState<any>(
        INITIAL_CITY_FORM_DATA
    );
    const [validation, setValidation] = React.useState<any>(INITIAL_VALIDATION);
    const [countryList, setCountryList] = React.useState<any>();
    const [title, setTitle] = React.useState<string>("");
    const [isModalOpened, setIsModalOpened] = React.useState<boolean>(false);
    const [code, setCode] = React.useState<any[]>([]);
    const listTva = useMemo(
        () =>
            getShopVatList(selectedRowShops).map((el) => ({
                label: el.value,
                value: el.id,
            })),
        [selectedRowShops]
    );

    async function addVille() {
        if (
            !resolveTvaFromList(
                cityFormData.tva,
                getShopVatList(selectedRowShops)
            )
        ) {
            ErrorToast(t("Invalid VAT rate"));
            return;
        }
        const localStCityLivraison = formatCityLivraisonData(cityData.data);
        const data = createDataVille(
            localStCityLivraison,
            cityFormData,
            code,
            selectedRowShops,
            franchiseID,
            false,
            selectedRows
        );
        setIsClickedBouttonValider(false);
        try {
            const responseData: any = await PostCity(data, fetchGlobal);
            if (responseData.error) {
                setDataIsUpdated!(true);
                throw Error("error");
            }

            const updatedDataPrice = {
                ...(shopStore.orders.dataPrice || {}),
                STVilleLivraison: data.data,
            };

            setOrders({ dataPrice: updatedDataPrice });
            toast.success(`${t("City added successfully")!}`, {
                position: toast.POSITION.TOP_CENTER,
                theme: "colored",
                icon: <CheckboxIcon height={25} width={25} fill="white" />,
            });
            setDataIsUpdated!(true);
            setIsEdited(false);
            setLocalFilterActive(false);
            setCode([]);
            clearData();
            clearMultiCustomSelectionData();
            setIsClickedBouttonValider(true);
        } catch (e) {
            ErrorToast(`${t("There's an error")!}`);
        }
    }
    async function editVille() {
        if (!isEdited) {
            setIsModalOpened(true);
        } else {
            if (
                !resolveTvaFromList(
                    cityFormData.tva,
                    getShopVatList(selectedRowShops)
                )
            ) {
                ErrorToast(t("Invalid VAT rate"));
                return;
            }
            const localStCityLivraison = formatCityLivraisonData(cityData.data);
            const data = createDataVille(
                localStCityLivraison,
                cityFormData,
                code,
                selectedRowShops,
                franchiseID,
                true,
                selectedRows
            );
            try {
                const responseData: any = await PostCity(data, fetchGlobal);
                if (responseData.error) {
                    setDataIsUpdated!(true);
                    throw Error("error");
                }

                const updatedDataPrice = {
                    ...(shopStore.orders.dataPrice || {}),
                    STVilleLivraison: data.data,
                };
                setOrders({ dataPrice: updatedDataPrice });
                toast.success(`${t("modification made successfully")!}`, {
                    position: toast.POSITION.TOP_CENTER,
                    theme: "colored",
                    icon: <CheckboxIcon height={25} width={25} fill="white" />,
                });
                setDataIsUpdated!(true);
                setIsEdited(false);
                setLocalFilterActive(false);
                setCode([]);
                clearData();
                setIsAddCity(!isAddCity);
                clearMultiCustomSelectionData();
                setValidation((prev: any) => ({
                    ...prev,
                    inputCode: false,
                    inputTva: false,
                    selectCountry: false,
                    isValidationNumber: false,
                    isValidationtMinimumAmount: false,
                    inputCity: false,
                }));
                setIsPlusIconClicked(false);
                setIsClickedBouttonValider(true);
            } catch (e) {
                ErrorToast(`${t("There's an error")!}`);
                setIsClickedBouttonValider(true);
            }
        }
    }
    const updateValidation = useCallback((field: string, value: boolean) => {
        setValidation((prev: any) => ({
            ...prev,
            [field]: value,
        }));
    }, []);

    const handleCountryChange = useCallback(
        (e: any) => {
            setCityFormData((prev: any) => ({
                ...prev,
                country: e?.label || "",
            }));
            updateValidation("selectCountry", false);
            setIsEdited(true);
            setIsClickedBouttonValider(true);
        },
        [updateValidation]
    );

    const handlePostalCodeChange = useCallback(
        (value: string | null, e: PostalCodeOption | PostalCodeOption[]) => {
            const option = Array.isArray(e) ? e[0] : e;
            if (value !== null && option) {
                const label = String(option.label ?? "");

                const [postalCode, cityName] = label.split(" - ");

                // 🚨 GUARD (bloquant fix)
                if (!postalCode || !cityName) {
                    ErrorToast("Invalid postal code format");
                    return;
                }

                const trimmedCode = postalCode.trim();
                const trimmedCity = cityName.trim();

                setCityFormData((prev: any) => ({
                    ...prev,
                    codePostal: trimmedCode,
                    city: trimmedCity,
                }));

                updateValidation(
                    "isExistingCodePostal",
                    listCode.includes(`${trimmedCode}_${trimmedCity}`)
                );
            } else {
                setCityFormData((prev: any) => ({
                    ...prev,
                    codePostal: "",
                    city: "",
                }));
            }

            setIsEdited(true);
            setValidation((prev: any) => ({
                ...prev,
                inputCode: false,
            }));
            updateValidation("inputCity", false);
            setIsClickedBouttonValider(true);
        },
        [listCode, updateValidation]
    );

    const handleTvaChange = useCallback(
        (e: any) => {
            setCityFormData((prev: any) => ({
                ...prev,
                tva: e?.label || "",
            }));
            setIsEdited(true);
            updateValidation("inputTva", false);

            setIsClickedBouttonValider(true);
        },
        [updateValidation]
    );

    const handleShippingCostsChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setIsClickedBouttonValider(true);
            setIsClickedBouttonValider(true);
            if (e.target.value.match(NUMBER_REGEX)) {
                setCityFormData((prev: any) => ({
                    ...prev,
                    shippingCosts: removeLeadingZeros(e.target.value),
                }));
                updateValidation("isValidationNumber", false);
                updateValidation("isDeliveryNull", false);
            } else {
                updateValidation("isValidationNumber", true);
                updateValidation("isDeliveryNull", false);
            }
            if (e.target.value === "") {
                setCityFormData((prev: any) => ({
                    ...prev,
                    shippingCosts: 0,
                }));
                updateValidation("isDeliveryNull", true);
                updateValidation("isValidationNumber", false);
            }
            setIsEdited!(true);
        },
        [updateValidation]
    );

    const handleMinimumAmountChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>) => {
            setIsClickedBouttonValider(true);
            if (e.target.value.match(NUMBER_REGEX)) {
                setCityFormData((prev: any) => ({
                    ...prev,
                    minimumAmount: removeLeadingZeros(e.target.value),
                }));
                updateValidation("isNullMinimumAmount", false);
                updateValidation("isValidationtMinimumAmount", false);
            } else {
                updateValidation("isNullMinimumAmount", false);
                updateValidation("isValidationtMinimumAmount", true);
            }
            if (e.target.value === "") {
                setCityFormData((prev: any) => ({
                    ...prev,
                    minimumAmount: 0,
                }));

                updateValidation("isNullMinimumAmount", true);
                updateValidation("isValidationtMinimumAmount", false);
            }
            setIsEdited!(true);
        },
        [updateValidation]
    );

    const handleCancel = useCallback(() => {
        if (isEdited) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsEdited(false);
                setSelectedRows([]);
                setIsAddCity(false);
            });
        } else {
            setSelectedRows([]);
            setIsAddCity(false);
        }
    }, [isEdited, isActionsClicked, setIsAddCity, setSelectedRows]);
    useEffect(() => {
        if (selectedRows.length > 0) {
            setTitle(t("Modify a city"));
            setCityFormData((prev: any) => ({
                ...prev,
                codePostal:
                    selectedRows[0][t("Postal code")] ||
                    selectedRows[0]["Code Postal"],
                city: selectedRows[0][t("City")],
                tva: list.includes(selectedRows[0]["Tva"].toString())
                    ? selectedRows[0]["Tva"]
                    : "",
                shippingCosts: selectedRows[0][t("Shipping costs")],
                minimumAmount:
                    selectedRows[0][t("Minimum amount to place an order")],
                country: selectedRows[0][t("Country")],
            }));
        } else {
            setTitle(t("Add a city"));
            setValidation(INITIAL_VALIDATION);
            setIsEdited(false);
            setIsClickedBouttonValider(true);
            setCityFormData({
                ...INITIAL_CITY_FORM_DATA,
                tva: resolveDefaultTva(getShopVatList(selectedRowShops)),
            });
        }
        fetchPays(pays, setCountryList).catch(() =>
            ErrorToast(t("Failed to load country or VAT data"))
        );
        // eslint-disable-next-line
    }, [isAddNewDeviseButtonClicked, selectedRows]);

    React.useEffect(() => {
        CountryPostalCodes(
            cityFormData.country,
            setCode,
            i18nextLng,
            fetchGlobal
        ).catch(() => ErrorToast(t("Failed to load postal codes")));
        // eslint-disable-next-line
    }, [cityFormData.country, setCode, i18nextLng]);

    const handleCloseForm = () => {
        if (isEdited === true) {
            setIsActionsClicked(!isActionsClicked);
            setActions(() => {
                setIsAddCity(!isAddCity);
                setIsEdited(false);
                clearData();
                clearMultiCustomSelectionData();
            });
        } else {
            setSelectedRows([]);
            setIsAddCity(!isAddCity);
            setIsEdited(false);
            clearData();
            clearMultiCustomSelectionData();
        }
    };

    return (
        <div
            style={{
                height: "63vh",
                display: "flex",
                flexDirection: "column",
                overflowY: "auto",
                paddingBottom: "20px",
            }}
        >
            <AvForm
                className="card"
                style={{ flex: 1, display: "flex", flexDirection: "column" }}
            >
                <CustomCardHeader
                    className="d-flex align-items-center justify-content-between"
                    style={{ height: "76px", flexShrink: 0 }}
                >
                    <h5 className="pt-3 dev_txt_FRtitleFormCurrency">
                        {title}
                    </h5>
                    <span
                        role="button"
                        aria-label={t("Close")}
                        tabIndex={0}
                        data-testid="city-form-close-btn"
                        className="cc-form-close-btn__clz"
                        onClick={handleCloseForm}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleCloseForm();
                            }
                        }}
                    >
                        <CrossIcon
                            className="icon-close-notification__clz dev_icn_FRcloseForm"
                            height={13}
                            width={13}
                            aria-hidden
                        />
                    </span>
                </CustomCardHeader>
                <CustomCardBody
                    className="pl-3 pr-5"
                    style={{
                        paddingBottom: "1rem",
                        flexGrow: 1,
                    }}
                >
                    <AvGroup>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("Country")}
                        </StyledLabel>

                        <StyledSelectInput
                            data-testid="city-country-select"
                            isClearable
                            autoFocus
                            noOptionsMessage={() => t("No options")}
                            options={countryList}
                            autocomplete="off"
                            className={classnames("", {
                                input__clz: validation.selectCountry,
                            })}
                            name="change"
                            onChange={handleCountryChange}
                            value={
                                cityFormData.country === ""
                                    ? null
                                    : {
                                          label: cityFormData.country,
                                          value: cityFormData.country,
                                      }
                            }
                            placeholder={`${t("Select")}…`}
                            type="text"
                        />
                        {validation.selectCountry && (
                            <ErrorMessage
                                message={t("Please select a country")}
                            />
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("Zip code")}
                        </StyledLabel>
                        <div>
                            <Select
                                data-testid="city-postal-code-select"
                                style={{ width: "100%", fontSize: "14px" }}
                                options={code}
                                className={classnames("", {
                                    input__clz: validation.inputCode,
                                })}
                                value={
                                    cityFormData.codePostal && cityFormData.city
                                        ? `${cityFormData.codePostal}_${cityFormData.city}`
                                        : undefined
                                }
                                showSearch={true}
                                onChange={handlePostalCodeChange}
                                optionLabelProp="postalLabel"
                                filterOption={postalCodeFilterOption}
                            />
                        </div>

                        {validation.inputCode && (
                            <ErrorMessage
                                message={t("Please select a  Zip code")}
                            />
                        )}
                        {validation.isExistingCodePostal && (
                            <ErrorMessage
                                message={
                                    t(
                                        "This city with this postal code already exists"
                                    )!
                                }
                            />
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("City")}
                        </StyledLabel>
                        <StyledTextInput
                            autocomplete="off"
                            name={t("City")}
                            value={cityFormData.city}
                            placeholder={t("City")}
                            className={classnames("not-allowed-input__clz", {
                                input__clz: validation.inputCity,
                            })}
                            type="text"
                        />

                        {validation.inputCity && (
                            <ErrorMessage message={t("Please select a City")} />
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel
                            htmlFor="text"
                            className="required__clz mt-3"
                        >
                            {t("TVA")}
                        </StyledLabel>
                        <StyledSelectInput
                            data-testid="city-tva-select"
                            isClearable
                            noOptionsMessage={() => t("No options")}
                            options={listTva}
                            autocomplete="off"
                            className={classnames("", {
                                input__clz: validation.inputTva,
                            })}
                            name="tva"
                            onChange={handleTvaChange}
                            value={
                                cityFormData.tva
                                    ? {
                                          label: cityFormData.tva,
                                          value: cityFormData.tva,
                                      }
                                    : null
                            }
                            placeholder={`${t("Select")}…`}
                            type="text"
                            maxMenuHeight="80px"
                        />
                        {validation.inputTva && (
                            <ErrorMessage
                                message={t("Please select a vat rate")}
                            />
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel className="mt-3" htmlFor="Shipping">
                            {t("Shipping costs")}
                        </StyledLabel>
                        <StyledTextInput
                            autocomplete="off"
                            id="remark"
                            name="remark"
                            value={cityFormData.shippingCosts}
                            type="text"
                            onChange={handleShippingCostsChange}
                            className={classnames("", {
                                input__clz:
                                    validation.isValidationNumber ||
                                    validation.isDeliveryNull,
                            })}
                        />

                        {validation.isValidationNumber && (
                            <ErrorMessage
                                message={t("Please insert a number")!}
                            />
                        )}
                        {validation.isDeliveryNull && (
                            <ErrorMessage
                                message={t("Please insert a delivery charge")!}
                            />
                        )}
                    </AvGroup>
                    <AvGroup>
                        <StyledLabel className="mt-3" htmlFor="remark">
                            {t("Minimum amount to place an order")}
                        </StyledLabel>
                        <StyledTextInput
                            autocomplete="off"
                            id="commande"
                            name="commande"
                            type="text"
                            onChange={handleMinimumAmountChange}
                            value={cityFormData.minimumAmount}
                            className={classnames("", {
                                input__clz:
                                    validation.isValidationtMinimumAmount ||
                                    validation.isNullMinimumAmount,
                            })}
                        />

                        {validation.isValidationtMinimumAmount && (
                            <ErrorMessage
                                message={t("Please insert a number")!}
                            />
                        )}
                        {validation.isNullMinimumAmount && (
                            <ErrorMessage
                                message={t("Please insert a Minimum Amount")!}
                            />
                        )}
                    </AvGroup>
                </CustomCardBody>
                <CustomCardFooter
                    className="p-8"
                    style={{
                        flexShrink: 0,
                        borderTop: "1px solid #dee2e6",
                    }}
                >
                    <CustomSecondaryColorButton
                        outline
                        rounded
                        className="mr-2 dev_btn_FRcancel"
                        variant="light"
                        onClick={handleCancel}
                    >
                        <span> {t("Cancel")}</span>
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        variant="primary"
                        className="dev_btn_FRvalidate"
                        disabled={!isClickedBouttonValider}
                        onClick={() => {
                            const isFormValid = validateAndSubmit(
                                cityFormData,
                                validation,
                                updateValidation
                            );
                            if (!isFormValid) {
                                scrollToFirstError();
                            }
                            if (isFormValid) {
                                if (selectedRows.length === 0) {
                                    addVille();
                                } else {
                                    editVille();
                                }
                            }
                        }}
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </CustomCardFooter>
            </AvForm>

            <ConfirmationEditMessage
                setDataIsUpdated={setDataIsUpdated}
                isModalOpened={isModalOpened}
                setIsModalOpened={setIsModalOpened}
                isAddClicked={isAddCity}
                setIsAddClicked={setIsAddCity}
                setEditedData={setSelectedRows}
            />
        </div>
    );
}
